import React, {
    createContext,
    use,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    type FC,
    type PropsWithChildren,
} from "react";
import { type GQL } from "@binale-tech/shared";
import { useApolloClient, useMutation } from "@apollo/client/react";
import { CompanyContext, YearPeriodContext } from "@domain/context/CompanyContext";
import { calendarEventsQuery } from "@domain/context/queries/calendarEventsQueries.graphql";
import { calendarEventDelete, calendarEventSave } from "@domain/context/mutations/calendarEventMutations.graphql";
import { useToastSaver } from "@ui-components/hooks/useToastSaver";

// SP1 of the calendar feature (FIBU-22). Mounted in the GLOBAL ContextProvider tree
// (see src/domain/context/index.tsx), NOT scoped around the home view - a future
// dedicated calendar page (SP4) shares this same fetch instead of refetching
// (design spec §14.5). Loads the selected year's events (YearPeriodContext, matching
// GlobalYearSelect); the app runs Apollo with `no-cache` everywhere (no normalized
// cache updates anywhere in this codebase), so state here is plain local `useState`,
// patched from mutation responses rather than via cache.modify/refetchQueries.

interface ICalendarEventsContext {
    events: GQL.ICalendarEvent[];
    loading: boolean;
}

export type CalendarEventSaveInput = Omit<GQL.ICalendarEventInput, "companyId">;

interface ICalendarEventsController {
    saveEvent: (input: CalendarEventSaveInput) => Promise<GQL.ICalendarEvent | undefined>;
    deleteEvent: (id: string) => Promise<void>;
}

export const CalendarEventsContext = createContext<ICalendarEventsContext>({ events: [], loading: false });
export const CalendarEventsControlContext = createContext<ICalendarEventsController>({
    saveEvent: () => Promise.reject(),
    deleteEvent: () => Promise.reject(),
});

export const CalendarEventsProvider: FC<PropsWithChildren> = ({ children }) => {
    const client = useApolloClient();
    const { companyGQL } = use(CompanyContext);
    const { year } = use(YearPeriodContext);
    const toastSaver = useToastSaver();
    const companyId = companyGQL?.id;

    const [events, setEvents] = useState<GQL.ICalendarEvent[]>([]);
    const [loading, setLoading] = useState(false);

    // Review fix (FIBU-22 final pass): guards against a stale response overwriting
    // fresher state - companyId/year can change (company switch, GlobalYearSelect)
    // while a slower in-flight request from the PREVIOUS company/year is still
    // pending; without this, that request could resolve after the new one and clobber
    // the newer, correct state with stale company-A/year-Y data.
    const epochRef = useRef(0);

    // Shared by fetchEvents (query variables) and saveEvent (post-save window
    // check) so both always agree on which year is currently loaded.
    const fetchWindow = useMemo(() => ({ from: `${year}-01-01`, to: `${year}-12-31` }), [year]);

    const [saveMutation] = useMutation(calendarEventSave);
    const [deleteMutation] = useMutation(calendarEventDelete);

    const fetchEvents = useCallback(async () => {
        const epoch = ++epochRef.current;
        if (!companyId) {
            setEvents([]);
            return;
        }
        setLoading(true);
        try {
            const res = await client.query({
                query: calendarEventsQuery,
                fetchPolicy: "no-cache",
                variables: { companyId, from: fetchWindow.from, to: fetchWindow.to },
            });
            if (epochRef.current !== epoch) {
                // A newer fetch has since started (company/year changed) - this response
                // is stale, drop it instead of overwriting fresher state.
                return;
            }
            setEvents(res.data.calendarEvents);
        } catch {
            if (epochRef.current === epoch) {
                setEvents([]);
            }
        } finally {
            if (epochRef.current === epoch) {
                setLoading(false);
            }
        }
    }, [client, companyId, fetchWindow]);

    useEffect(() => {
        fetchEvents();
    }, [fetchEvents]);

    const saveEvent = useCallback(
        async (input: CalendarEventSaveInput) => {
            if (!companyId) {
                return undefined;
            }
            // Same stale-response guard as fetchEvents' epochRef above, applied to the
            // post-mutation patch: capture the epoch this save was dispatched under so a
            // mutation that resolves AFTER a company/year switch (fetchEvents bumps the
            // epoch on every companyId/year change) doesn't inject its result into the
            // now-active company/year's state - the next fetchEvents for the new
            // company/year is authoritative instead.
            const dispatchEpoch = epochRef.current;
            const saved = await toastSaver(() =>
                saveMutation({ variables: { input: { ...input, companyId } } }).then(
                    (res) => res.data?.calendarEventSave,
                ),
            );
            if (saved && epochRef.current === dispatchEpoch) {
                // The provider only holds the currently-fetched year window; a save
                // that moved the event's date outside of it (or created it outside
                // of it) must not linger in local state - drop it instead.
                const inWindow = saved.eventDate >= fetchWindow.from && saved.eventDate <= fetchWindow.to;
                setEvents((prev) => {
                    const idx = prev.findIndex((e) => e.id === saved.id);
                    if (!inWindow) {
                        return idx === -1 ? prev : prev.filter((e) => e.id !== saved.id);
                    }
                    if (idx === -1) {
                        return [...prev, saved];
                    }
                    const next = [...prev];
                    next[idx] = saved;
                    return next;
                });
            }
            return saved;
        },
        [companyId, saveMutation, toastSaver, fetchWindow],
    );

    const deleteEvent = useCallback(
        async (id: string) => {
            if (!companyId) {
                return;
            }
            // Same epoch guard as saveEvent above (see its comment).
            const dispatchEpoch = epochRef.current;
            const deleted = await toastSaver(() =>
                deleteMutation({ variables: { input: { id, companyId } } }).then(
                    (res) => res.data?.calendarEventDelete,
                ),
            );
            if (deleted && epochRef.current === dispatchEpoch) {
                setEvents((prev) => prev.filter((e) => e.id !== id));
            }
        },
        [companyId, deleteMutation, toastSaver],
    );

    const controller: ICalendarEventsController = useMemo(() => ({ saveEvent, deleteEvent }), [saveEvent, deleteEvent]);
    const value: ICalendarEventsContext = useMemo(() => ({ events, loading }), [events, loading]);

    return (
        <CalendarEventsContext value={value}>
            <CalendarEventsControlContext value={controller}>{children}</CalendarEventsControlContext>
        </CalendarEventsContext>
    );
};
